import React, { createContext, useContext, useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { jwtDecode } from "jwt-decode";
import { GET_CLIENT, GET_CLIENT_BY_PHONE, GET_MY_CLIENT, type JWTDecoded } from "../pages/authentication";

interface AuthContextType {
  isAuthenticated: boolean;
  user: any | null; // Replace 'any' with your Client type
  loading: boolean;
  logout: () => void;
  /** Resolves when the profile (and so the permissions) has been re-fetched; `await` it before navigating to a permission-gated route. */
  refreshAuth: () => Promise<any> | void;
}

// Exported so tests can wrap a subject in AuthContext.Provider with a stub value.
// App code should always go through useAuth() rather than consuming this directly.
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userPhone, setUserPhone] = useState<string | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);

  // Self-service: the backend's `myClient` query derives the current user
  // from the JWT and has no ADMIN gate — required so non-admin employees
  // (cooks, carers, any USER+EMPLOYEE baseline) can populate AuthContext on
  // login. Previous GET_CLIENT_BY_ID path was ADMIN-gated and locked them out.
  // Also removes the `{ id: clientId }` null-race that triggered "Variable
  // $id must not be null" errors when the query mounted before JWT decode.
  const { data: clientIdData, error: clientIdError, refetch: refetchClientById } = useQuery(GET_MY_CLIENT, {
    skip: !isAuthenticated,
  });

  // FALLBACK: Query for client data when we have an email (backward compatibility)
  const { data: clientEmailData } = useQuery(GET_CLIENT, {
    variables: { email: userEmail },
    skip: !userEmail || !!clientId,  // Skip if we have clientId (more efficient)
  });

  // FALLBACK: Query for client data when we have a phone number (backward compatibility)
  const { data: clientPhoneData } = useQuery(GET_CLIENT_BY_PHONE, {
    variables: { phoneNumber: userPhone },
    skip: !userPhone || !!clientId,  // Skip if we have clientId (more efficient)
  });

  // Handle query results with useEffect instead of onCompleted/onError
  useEffect(() => {
    if (clientIdData?.myClient) {
      console.log("[1] AuthContext - Client data fetched via myClient:", {
        clientId: clientIdData.myClient.id,
        email: clientIdData.myClient.email,
        name: `${clientIdData.myClient.fName} ${clientIdData.myClient.lName}`,
        permissions: clientIdData.myClient.permissions?.length || 0,
      });
      setUser(clientIdData.myClient);
      setLoading(false);
    }
  }, [clientIdData]);

  useEffect(() => {
    if (clientIdError) {
      console.error("[2] AuthContext - Error fetching myClient:", clientIdError);
      setLoading(false);
      // Fall back to email/phone query
      setClientId(null);
    }
  }, [clientIdError]);

  useEffect(() => {
    if (clientEmailData?.clientByEmail) {
      console.log("[3] AuthContext - Client data fetched by email (FALLBACK):", clientEmailData.clientByEmail.email);
      setUser(clientEmailData.clientByEmail);
      setLoading(false);
    }
  }, [clientEmailData]);

  useEffect(() => {
    if (clientPhoneData?.clientByPhone) {
      console.log("[4] AuthContext - Client data fetched by phone (FALLBACK):", clientPhoneData.clientByPhone.phoneNumber);
      setUser(clientPhoneData.clientByPhone);
      setLoading(false);
    }
  }, [clientPhoneData])

  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    if (token) {
      try {
        const decoded = jwtDecode<JWTDecoded>(token);
        setIsAuthenticated(true);

        console.log("[5] AuthContext - JWT decoded:", {
          hasClientId: !!decoded.clientId,
          hasEmail: !!decoded.email,
          hasPhoneNumber: !!decoded.phoneNumber,
          hasTenantId: !!decoded.tenantId
        });

        // Best practice: Use clientId if available (most efficient)
        if (decoded.clientId) {
          setClientId(decoded.clientId);
          console.log("[6] AuthContext - Using clientId from JWT:", decoded.clientId);
          // This will trigger the GET_CLIENT_BY_ID query
        }

        // Store email/phone as fallback
        if (decoded.email) {
          setUserEmail(decoded.email);
          console.log("[7] AuthContext - Email from JWT (fallback):", decoded.email);
        } else if (decoded.phoneNumber) {
          setUserPhone(decoded.phoneNumber);
          console.log("[8] AuthContext - Phone from JWT (fallback):", decoded.phoneNumber);
        }
      } catch (error) {
        console.error("[9] AuthContext - Invalid token:", error);
        logout();
      }
    } else {
      setLoading(false);
    }
  }, []);

  const logout = () => {
    localStorage.removeItem("auth_token");
    setIsAuthenticated(false);
    setUser(null);
    setUserEmail(null);
    setUserPhone(null);
    setClientId(null);
    console.log("[10] AuthContext - User logged out");
  };

  // Folded 2026-09-11 from the two tenants that were ahead of the leader:
  // - SurgReady: returns the myClient refetch so callers can `await refreshAuth()` and be sure the
  //   permissions are fresh (e.g. after a subscription grants LM_STUDENT) before navigating.
  // - Life Essentials Club: state is only SET when a value changed, to avoid needless re-renders.
  // The profile is ALWAYS re-fetched, even when the account id is unchanged — a refresh with the
  // same id is exactly how newly granted permissions arrive.
  const refreshAuth = (): Promise<any> | void => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const decoded = jwtDecode<JWTDecoded>(token);
      setIsAuthenticated(true);

      let refetchPromise: Promise<any> | undefined;
      if (decoded.clientId) {
        if (decoded.clientId !== clientId) setClientId(decoded.clientId);
        if (refetchClientById) refetchPromise = refetchClientById();
      }

      if (decoded.email) {
        if (decoded.email !== userEmail) setUserEmail(decoded.email);
      } else if (decoded.phoneNumber && decoded.phoneNumber !== userPhone) {
        setUserPhone(decoded.phoneNumber);
      }

      return refetchPromise;
    } catch (error) {
      console.error("[16] AuthContext - Invalid token during refresh:", error);
      logout();
    }
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    logout,
    refreshAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
